import React from 'react';
import styled from 'styled-components';

import { ApiOrder } from 'api/types';
import TextColored from 'components/basic/text/TextColored';
import MyTradeClickableStatus from 'components/trading/MyTrades/MyTradeClickableStatus';
import { formatDateTime } from 'lib/date';
import {
  formatNumber,
  formatPrice,
  formatUsd,
  formatUsdWithSign,
} from 'lib/display/formatting';
import { capitalizeFirstLetter } from 'lib/string';
import { Chains } from 'reduxState/exchange/exchangeActiveChainUtils';

const ItemWrap = styled.div<{ $isHighlighted: boolean }>`
  padding: 10px;
  margin: 10px 0;
  line-height: 1.5;
  border-radius: 5px;
  background: var(--black1b);
  ${props =>
    props.$isHighlighted
      ? 'border: 1px solid color-mix(in srgb, var(--main) 50%, transparent);'
      : ''}
`;

const ValuesGrid = styled.div`
  clear: both;
  display: grid;
  grid-template-columns: fit-content(100%) 1fr;
  grid-gap: 5px;
  grid-column-gap: 15px;
`;

const Label = styled.div`
  font-weight: bold;
`;

type Props = {
  baseSymbol: string;
  quoteSymbol: string;
  orderFills: ApiOrder['fills'];
  highlightedFillId?: string;
  tickSizeDecimals: number;
  stepSizeDecimals: number;
  activeChain: Chains;
};

export default function OrderDetailFills({
  baseSymbol,
  quoteSymbol,
  orderFills,
  highlightedFillId,
  tickSizeDecimals,
  stepSizeDecimals,
  activeChain,
}: Props) {
  return (
    <>
      {(orderFills || []).map(fill => (
        <ItemWrap
          key={fill.fillId}
          $isHighlighted={
            !!highlightedFillId && highlightedFillId === fill.fillId
          }
        >
          <b style={{ display: 'inline-block', paddingBottom: 5 }}>
            {formatDateTime(fill.time)}
          </b>
          <div style={{ float: 'right' }}>
            <MyTradeClickableStatus fill={fill} activeChain={activeChain} />
          </div>
          <br />
          <ValuesGrid>
            <Label>Quantity</Label>
            <div>
              {formatNumber(fill.quantity, { fixedDecimals: stepSizeDecimals })}{' '}
              {baseSymbol}
            </div>

            <Label>Price</Label>
            <div>
              {formatPrice(fill.price, tickSizeDecimals)} {quoteSymbol}
            </div>

            <Label>Trade Fee</Label>
            <div>{formatUsd(fill.fee)}</div>

            <Label>Liquidity</Label>
            <div>{capitalizeFirstLetter(fill.liquidity)}</div>

            <Label>Realized P&L</Label>
            <div>
              <TextColored
                color={Number(fill.realizedPnL) > 0 ? 'green' : 'red'}
              >
                {formatUsdWithSign(fill.realizedPnL)}
              </TextColored>
            </div>
          </ValuesGrid>
        </ItemWrap>
      ))}
    </>
  );
}
